import { IconButton } from '@zillow/constellation-10';
import { IconStar0Percent, IconStar100Percent } from '@zillow/constellation-icons';
import { useAuth } from '@zg-rentals/auth-comet';
import { IconButtonTooltip } from '@zg-rentals/satellite-components';
import type React from 'react';
import { useCallback } from 'react';
import {
  emitStarConversationEvent,
  STAR_CONVERSATION_EVENT_TYPE_ID,
} from '../../../constants/clickstreamEvents/starConversations';
import useConversationActions from '../../../hooks/useConversationActions';
import StatusLabel from '../../StatusLabel';

type ConversationItemFooterProps = {
  conversationId: string;
  isFavorite: boolean;
  isStarConversationIconDisabled: boolean;
  statusLabel?: { linkId: string; text: string } | null;
};

export default function ConversationItemFooter({
  conversationId,
  isFavorite,
  isStarConversationIconDisabled,
  statusLabel,
}: ConversationItemFooterProps) {
  const { user } = useAuth();
  const { markConversationAsFavoriteOrUnfavorite } = useConversationActions();

  const handleUpdateFavorite = useCallback(
    (event: React.MouseEvent, isFavorited: boolean) => {
      event.stopPropagation();

      if (!isFavorited) {
        emitStarConversationEvent(user);
      }

      return markConversationAsFavoriteOrUnfavorite(conversationId, isFavorited);
    },
    [user, conversationId, markConversationAsFavoriteOrUnfavorite],
  );

  return (
    <>
      {!!statusLabel && <StatusLabel linkId={statusLabel.linkId} text={statusLabel.text} />}

      <IconButtonTooltip
        content="Star conversation"
        trigger={
          <IconButton
            data-eventid={STAR_CONVERSATION_EVENT_TYPE_ID}
            shape="circle"
            emphasis="bare"
            tone={isFavorite ? 'brand' : 'neutral'}
            disabled={isStarConversationIconDisabled}
            icon={isFavorite ? <IconStar100Percent /> : <IconStar0Percent />}
            onClick={(event: React.MouseEvent) => handleUpdateFavorite(event, isFavorite)}
            title="star"
            css={{
              marginLeft: 'auto',
              marginRight: '-tight', // Align the star icon with content on the right
              marginBlock: '-tighter',
            }}
          />
        }
      />
    </>
  );
}
